import React, { useEffect, useMemo, useState } from "react";
import {
    CalendarDays,
    FileText,
    Search,
    Upload,
    RefreshCw,
    ExternalLink,
    Save,
    Truck,
} from "lucide-react";
import TopBar from "./TopBar";

const PERIOD_OPTIONS = [
    { value: "day", label: "Oggi" },
    { value: "week", label: "Ultimi 7 giorni" },
    { value: "month", label: "Ultimi 30 giorni" },
    { value: "custom", label: "Personalizzato" },
];

const CATEGORIE = [
    { value: "REFRIGERATI", label: "Refrigerati" },
    { value: "SURGELATI", label: "Surgelati" },
    { value: "SECCHI", label: "Secchi" },
    { value: "ALTRO", label: "Altro" },
];

const TIPI = [
    { value: "NESSUNO", label: "Nessuno" },
    { value: "CARNE", label: "Carne" },
    { value: "PESCE", label: "Pesce" },
];

function formatDate(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("it-IT");
}

function formatDateTime(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("it-IT");
}

export default function ArchivioDdt() {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [items, setItems] = useState([]);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const [period, setPeriod] = useState("month");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [search, setSearch] = useState("");

    const [form, setForm] = useState({
        ddt: "",
        data: "",
        categoria: "ALTRO",
        tipo_prodotto: "NESSUNO",
        fornitore: "",
        note: "",
        ddt_file_path: "",
        ddt_file_url: "",
    });

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        p.set("period", period);
        if (period === "custom") {
            if (dateFrom) p.set("date_from", dateFrom);
            if (dateTo) p.set("date_to", dateTo);
        }
        if (search.trim()) p.set("search", search.trim());
        return p.toString();
    }, [period, dateFrom, dateTo, search]);

    async function loadItems() {
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`/api/ricevimento_merci_list.php?${queryString}`, {
                credentials: "include",
            });
            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || "Errore nel caricamento DDT");
            }

            setItems(Array.isArray(json.items) ? json.items : []);
        } catch (err) {
            setError(err.message || "Errore nel caricamento DDT");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadItems();
    }, [queryString]);

    async function handleUploadFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError("");
        setSuccessMsg("");

        try {
            const fd = new FormData();
            fd.append("file", file);

            const res = await fetch("/api/ricevimento_merci_ddt_upload.php", {
                method: "POST",
                body: fd,
                credentials: "include",
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || "Errore upload file");
            }

            setForm((prev) => ({
                ...prev,
                ddt_file_path: json.file_path || "",
                ddt_file_url: json.file_url || "",
            }));

            setSuccessMsg("File caricato correttamente");
        } catch (err) {
            setError(err.message || "Errore upload file");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    async function handleSave(e) {
        e.preventDefault();

        setSaving(true);
        setError("");
        setSuccessMsg("");

        try {
            const payload = {
                ddt: form.ddt,
                data: form.data,
                categoria: form.categoria,
                tipo_prodotto: form.tipo_prodotto,
                fornitore: form.fornitore,
                note: form.note,
                esito: "CONFORME",
                ddt_file_path: form.ddt_file_path,
                etichette_paths: [],
            };

            const res = await fetch("/api/ricevimento_merci_save.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify(payload),
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || "Errore salvataggio");
            }

            setSuccessMsg("DDT salvato correttamente");
            setForm({
                ddt: "",
                data: "",
                categoria: "ALTRO",
                tipo_prodotto: "NESSUNO",
                fornitore: "",
                note: "",
                ddt_file_path: "",
                ddt_file_url: "",
            });

            await loadItems();
        } catch (err) {
            setError(err.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <TopBar />

            <div className="p-4 md:p-6 space-y-6">
                <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Archivio DDT</h1>
                            <p className="text-sm text-slate-500 mt-1">
                                Carica nuovi DDT e consulta tutti quelli già registrati per la tua azienda.
                            </p>
                        </div>

                        <button
                            onClick={loadItems}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50"
                        >
                            <RefreshCw size={16} />
                            Aggiorna
                        </button>
                    </div>
                </div>

                {(error || successMsg) && (
                    <div className="space-y-3">
                        {error && (
                            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}
                        {successMsg && (
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                                {successMsg}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2 space-y-6">
                        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <CalendarDays size={18} className="text-slate-500" />
                                <h2 className="text-lg font-semibold text-slate-900">Filtri</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Periodo
                                    </label>
                                    <select
                                        value={period}
                                        onChange={(e) => setPeriod(e.target.value)}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                    >
                                        {PERIOD_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {period === "custom" && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                                Dal
                                            </label>
                                            <input
                                                type="date"
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                                Al
                                            </label>
                                            <input
                                                type="date"
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                            />
                                        </div>
                                    </>
                                )}

                                <div className={period === "custom" ? "" : "md:col-span-3"}>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Cerca
                                    </label>
                                    <div className="relative">
                                        <Search
                                            size={16}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        />
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Cerca DDT, fornitore, categoria..."
                                            className="w-full rounded-xl border border-slate-300 pl-9 pr-3 py-2 text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <FileText size={18} className="text-slate-500" />
                                <h2 className="text-lg font-semibold text-slate-900">DDT caricati</h2>
                            </div>

                            {loading ? (
                                <div className="text-sm text-slate-500">Caricamento...</div>
                            ) : items.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
                                    Nessun DDT trovato nel periodo selezionato.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {items.map((item) => (
                                        <div
                                            key={item.id}
                                            className="rounded-2xl border border-slate-200 p-4 hover:border-slate-300 transition"
                                        >
                                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                                                            ID #{item.id}
                                                        </span>
                                                        <span className="text-base font-semibold text-slate-900">
                                                            {item.ddt || "DDT senza numero"}
                                                        </span>
                                                        {item.nc_id ? (
                                                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                                                                NC collegata
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 text-sm text-slate-600">
                                                        <div>
                                                            <span className="font-medium text-slate-800">Data:</span>{" "}
                                                            {formatDate(item.data)}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-800">Fornitore:</span>{" "}
                                                            {item.fornitore || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-800">Categoria:</span>{" "}
                                                            {item.categoria || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-800">Tipo:</span>{" "}
                                                            {item.tipo_prodotto || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-800">Esito:</span>{" "}
                                                            {item.esito || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-800">Creato:</span>{" "}
                                                            {formatDateTime(item.created_at)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex gap-2">
                                                    {item.ddt_file_url ? (
                                                        <a
                                                            href={item.ddt_file_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700"
                                                        >
                                                            <ExternalLink size={16} />
                                                            Apri file
                                                        </a>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-400">
                                                            Nessun file
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5 sticky top-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Truck size={18} className="text-slate-500" />
                                <h2 className="text-lg font-semibold text-slate-900">Nuovo DDT</h2>
                            </div>

                            <form onSubmit={handleSave} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Numero DDT
                                    </label>
                                    <input
                                        type="text"
                                        value={form.ddt}
                                        onChange={(e) => setForm((s) => ({ ...s, ddt: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Data
                                    </label>
                                    <input
                                        type="date"
                                        value={form.data}
                                        onChange={(e) => setForm((s) => ({ ...s, data: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Fornitore
                                    </label>
                                    <input
                                        type="text"
                                        value={form.fornitore}
                                        onChange={(e) => setForm((s) => ({ ...s, fornitore: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Categoria
                                    </label>
                                    <select
                                        value={form.categoria}
                                        onChange={(e) => setForm((s) => ({ ...s, categoria: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                    >
                                        {CATEGORIE.map((c) => (
                                            <option key={c.value} value={c.value}>
                                                {c.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Tipo prodotto
                                    </label>
                                    <select
                                        value={form.tipo_prodotto}
                                        onChange={(e) =>
                                            setForm((s) => ({ ...s, tipo_prodotto: e.target.value }))
                                        }
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                                    >
                                        {TIPI.map((t) => (
                                            <option key={t.value} value={t.value}>
                                                {t.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Note
                                    </label>
                                    <textarea
                                        value={form.note}
                                        onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
                                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm min-h-[90px]"
                                        placeholder="Note facoltative..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        File DDT
                                    </label>

                                    <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm cursor-pointer hover:bg-slate-50">
                                        <Upload size={16} />
                                        {uploading ? "Caricamento..." : "Carica PDF o immagine"}
                                        <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                                            onChange={handleUploadFile}
                                            className="hidden"
                                        />
                                    </label>

                                    {form.ddt_file_url && (
                                        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                                            <span className="truncate text-slate-700">
                                                File caricato correttamente
                                            </span>
                                            <a
                                                href={form.ddt_file_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                                            >
                                                <ExternalLink size={14} />
                                                Apri
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
                                >
                                    <Save size={16} />
                                    {saving ? "Salvataggio..." : "Salva DDT"}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}